import { useEffect, useCallback } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Lightbox — modal de imagem em tela cheia com fundo embaçado. Clica numa
 * imagem da galeria, abre por cima da página atual (não vai pro arquivo
 * direto). Se for uma sequência (carrossel), tem setas pra navegar.
 *
 * Controles: Esc fecha; ←/→ navegam; clicar fora fecha.
 */
export interface LightboxImage {
  src: string;
  alt?: string;
  caption?: string;
}

export function ImageLightbox({
  images,
  index,
  onClose,
  onChange,
}: {
  images: LightboxImage[];
  /** null = fechado; número = índice da imagem aberta. */
  index: number | null;
  onClose: () => void;
  onChange: (next: number) => void;
}) {
  const isOpen = index !== null && images.length > 0;
  const total = images.length;
  const safeIdx = index ?? 0;

  const goPrev = useCallback(() => {
    if (total === 0) return;
    onChange((safeIdx - 1 + total) % total);
  }, [safeIdx, total, onChange]);
  const goNext = useCallback(() => {
    if (total === 0) return;
    onChange((safeIdx + 1) % total);
  }, [safeIdx, total, onChange]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") goPrev();
      else if (e.key === "ArrowRight") goNext();
    };
    window.addEventListener("keydown", onKey);
    // Trava o scroll do body enquanto aberto.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, onClose, goPrev, goNext]);

  if (!isOpen) return null;
  const img = images[safeIdx]!;

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(10, 12, 20, 0.75)",
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
        padding: 24,
      }}
    >
      {/* Botão fechar (canto superior direito) */}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        aria-label="Fechar"
        style={{
          position: "absolute",
          top: 18,
          right: 18,
          width: 40,
          height: 40,
          borderRadius: 999,
          border: "none",
          background: "rgba(255,255,255,0.12)",
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          backdropFilter: "blur(6px)",
        }}
      >
        <X size={18} />
      </button>

      {/* Seta anterior */}
      {total > 1 && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); goPrev(); }}
          aria-label="Anterior"
          style={{
            position: "absolute",
            left: 18,
            top: "50%",
            transform: "translateY(-50%)",
            width: 48,
            height: 48,
            borderRadius: 999,
            border: "none",
            background: "rgba(255,255,255,0.12)",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
          }}
        >
          <ChevronLeft size={22} />
        </button>
      )}

      {/* Imagem central */}
      <img
        src={img.src}
        alt={img.alt ?? ""}
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: "min(90vw, 1100px)",
          maxHeight: "85vh",
          objectFit: "contain",
          borderRadius: 8,
          boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
          background: "#000",
        }}
      />

      {/* Seta próxima */}
      {total > 1 && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); goNext(); }}
          aria-label="Próxima"
          style={{
            position: "absolute",
            right: 18,
            top: "50%",
            transform: "translateY(-50%)",
            width: 48,
            height: 48,
            borderRadius: 999,
            border: "none",
            background: "rgba(255,255,255,0.12)",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
          }}
        >
          <ChevronRight size={22} />
        </button>
      )}

      {/* Indicador/legenda no rodapé */}
      <div
        style={{
          position: "absolute",
          bottom: 22,
          left: 0,
          right: 0,
          display: "flex",
          justifyContent: "center",
          pointerEvents: "none",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "8px 14px",
            borderRadius: 999,
            background: "rgba(0,0,0,0.45)",
            color: "rgba(255,255,255,0.9)",
            fontSize: 12.5,
            fontFamily: "var(--wf-font-body)",
            backdropFilter: "blur(6px)",
          }}
        >
          {total > 1 && <span style={{ fontWeight: 700 }}>{safeIdx + 1}/{total}</span>}
          {img.caption && <span style={{ opacity: 0.85 }}>{img.caption}</span>}
        </div>
      </div>
    </div>
  );
}
